import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Formik } from 'formik';
import { Collapse } from 'react-bootstrap';
import ReactDOMServer from 'react-dom/server';
import {
  AppDatePicker,
  AppInputNumber,
  AppInput,
  AppRemixIcon,
  AppSingleSelect,
  AppNoInfo,
  AppNotificationAlert,
} from '@/layout/AppComponents';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import Translator from '../../../layout/components/helpers/Translator';
import images from '../../../constants/images';
import AppLink from '../../../routing/helpers/AppLink';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import NoInfo from '../../../layout/components/helpers/NoInfo';
import SpinnerLoader from '../../../layout/components/helpers/SpinnerLoader';
import { path, axiosPath } from '../../../routing/helpers/router';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import _ from 'lodash';

const ModuleChemicalTradingProductListScreen = () => {
  const [moduleData, setModuleData] = useState();
  const { moduleBridgeHex } = useParams();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const global = useSelector((state) => state.global);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState(null);
  const [productOptions, setProductOptions] = useState(null);
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [actualFilters, setActualFilters] = useState(null);
  const [filtersFormInitialValues] = useState({
    isOnlyFavourites: false,
    isOnlyOwn: false,
    isOnlyArchives: false,
    archivesToo: false,
    company: '',
    comercialName: '',
    chemicalName: '',
    containerType: null,
    quantityFrom: null,
    quantityTo: null,
    dateFrom: null,
    dateTo: null,
    expireDateFrom: null,
    expireDateTo: null,
  });

  // > FILTERS
  const handleFilterForm = (values) => {
    setActualFilters(values);
    setLoading(true);
  };
  const handleRemoveFilters = (resetForm) => {
    resetForm();
    setActualFilters(null);
    setLoading(true);
  };
  // < FILTERS

  // > LOAD PRODUCTS //
  useEffect(() => {
    if (loading) {
      axios
        .post(
          axiosPath('api_r_user_module_chemicalTrading_products_get_list'),
          { moduleBridgeHex, filters: actualFilters },
        )
        .then((r) => {
          if (isMounted.current) {
            setProducts(r.data);
            setIsFiltersPanelOpen(false);
            setLoading(false);
          }
        });
    }
  }, [loading]);
  // < LOAD PRODUCTS //

  // > LOAD PRODUCTS OPTIONS //
  useEffect(() => {
    axios
      .post(axiosPath('api_r_user_module_chemicalTrading_product_options'), {
        moduleBridgeHex,
      })
      .then((r) => {
        if (isMounted.current) {
          setProductOptions(r.data);
        }
      });
  }, []);
  // > LOAD PRODUCTS OPTIONS //

  // > LOAD MODULE DATA //
  useEffect(() => {
    axios
      .post(axiosPath('api_r_user_module_chemicalTrading_getModuleData'), {
        moduleBridgeHex,
      })
      .then((r) => {
        if (isMounted.current) setModuleData(r.data);
      });
  }, []);
  // > LOAD MODULE DATA //

  // < TOGGLE FAVOURITE PRODUCT
  const toggleFavourite = async (id) => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_chemicalTrading_product_toggle_favourite'),
        { id, moduleBridgeHex },
      );
      return response.data;
    } catch (err) {}
  };

  const handleToggleFavourite = (id) => {
    toggleFavourite(id)
      .then((res) => {
        if (isMounted.current) {
          const updatedProducts = [...products];
          const productIndex = _.findIndex(products, { id });
          updatedProducts[productIndex].isFavourite = res.isFavourite;
          setProducts(updatedProducts);
          AppNotificationAlert({
            title: 'Completado',
            message: res.isFavourite
              ? 'Producto marcado como favorito'
              : 'Producto desmarcado como favorito',
            type: 'success',
            duration: 1000,
          });
        }
      })
      .catch((e) => {
        AppNotificationAlert({
          title: 'Error',
          message: 'Se ha producido un error',
          type: 'danger',
          duration: 1000,
        });
      });
  };
  // < TOGGLE FAVOURITE PRODUCT

  const columns = [
    {
      selector: (row) => row.image,
      width: '120px',
      cell: (product) => <Image src={product.image || images.defaultProduct} />,
    },
    {
      name: 'Empresa',
      selector: (row) => row.company,
      sortable: true,
    },
    {
      name: 'Nombre comercial',
      selector: (row) => row.comercialName,
      sortable: true,
    },
    {
      name: 'Nombre químico',
      selector: (row) => row.chemicalName,
      sortable: true,
    },
    {
      name: 'Cantidad (kg)',
      selector: (row) => row.quantity,
      sortable: true,
    },
    {
      name: 'F. subida',
      selector: (row) => row.date,
      sortable: true,
      width: '120px',
    },
    {
      name: 'F. caducidad',
      selector: (row) => row.expireDate,
      sortable: true,
      width: '120px',
    },
    {
      name: 'Archivos',
      selector: (row) => row.files,
      width: '100px',
      ignoreRowClick: true,
      cell: (product) =>
        product.files && Object.keys(product.files).length
          ? Object.keys(product.files).map((salt, index) => (
              <a
                key={index}
                target="_blank"
                title={product.files[salt].name}
                className="mr-1"
                href={`${global.urlData.userApiUrl}m/chemical-trading/get-product-file/${moduleBridgeHex}/${product.id}/${salt}`}
                rel="noreferrer"
              >
                <AppRemixIcon icon="file" className="align-middle" />
              </a>
            ))
          : '',
    },
    {
      selector: (row) => row.options,
      width: '100px',
      ignoreRowClick: true,
      cell: (product) => (
        <OptionsCell>
          {product.isOwn && (
            <AppLink
              path="user_module_chemicalTrading_product_edit"
              params={{ moduleBridgeHex, id: product.id }}
              className="btn btn-outline-info btn-xs mr-1"
              title={ReactDOMServer.renderToString(
                <Translator
                  file="module-chemicalTrading"
                  name="edit.product"
                />,
              )}
            >
              <AppRemixIcon icon="edit-box" className="align-middle" />
            </AppLink>
          )}
          <span
            className={`btn btn${product.isFavourite ? '' : '-outline'}-warning btn-xs`}
            onClick={() => handleToggleFavourite(product.id)}
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-chemicalTrading"
                name={
                  product.isFavourite
                    ? 'remove.from.favourites'
                    : 'add.to.favourites'
                }
              />,
            )}
          >
            <AppRemixIcon
              icon="star"
              filled={product.isFavourite}
              className="align-middle"
            />
          </span>
        </OptionsCell>
      ),
    },
  ];

  const conditionalRowStyles = [
    {
      when: (product) => product.isOwn,
      style: {
        backgroundColor: '#efffee',
      },
    },
    {
      when: (product) => product.isArchived,
      style: {
        backgroundColor: '#ffeef0',
      },
    },
  ];

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleBridgeHex
            ? [
                {
                  name: `${
                    moduleData.moduleName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-chemicalTrading"
                      name="products.list"
                    />,
                  )}`,
                },
              ]
            : [{ name: '...' }]
        }
      />
      <Formik
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize
        initialValues={filtersFormInitialValues}
        onSubmit={handleFilterForm}
      >
        {({ setFieldValue, resetForm, handleSubmit, values, errors }) => (
          <>
            <section className="content">
              <div className="row">
                <div className="col-lg-12 mt-3 text-center">
                  <ClientLogo src={moduleData.clientLogoUrl} />
                </div>
              </div>
              <div className="row">
                <div className="col-lg-12 mt-3">
                  <AppLink
                    path="user_module_chemicalTrading_product_new"
                    params={{ moduleBridgeHex }}
                    className="btn btn-success btn-sm mr-2"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-chemicalTrading"
                        name="add.product"
                      />,
                    )}
                  >
                    {ReactDOMServer.renderToString(
                      <Translator
                        file="module-chemicalTrading"
                        name="add.product"
                      />,
                    )}{' '}
                    <AppRemixIcon icon="add" className="align-bottom" />
                  </AppLink>
                  <span
                    className={`btn btn-${
                      isFiltersPanelOpen ? '' : 'outline-'
                    }info btn-sm float-right`}
                    onClick={() => setIsFiltersPanelOpen(!isFiltersPanelOpen)}
                  >
                    Filtros{' '}
                    <AppRemixIcon icon="filter" className="align-bottom" />
                  </span>
                  {actualFilters && (
                    <span
                      className="btn btn-danger btn-sm mr-1 float-right"
                      onClick={() => handleRemoveFilters(resetForm)}
                    >
                      <Translator
                        file="module-chemicalTrading"
                        name="remove.filters"
                      />
                    </span>
                  )}
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-12">
                  <Collapse in={isFiltersPanelOpen}>
                    <div className="card mb-0">
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-3 text-md-center">
                            <label className="col-form-label mr-3 pointer">
                              <input
                                type="checkbox"
                                className="mr-1"
                                value={values.isOnlyFavourites}
                                onChange={() =>
                                  setFieldValue(
                                    'isOnlyFavourites',
                                    !values.isOnlyFavourites,
                                  )
                                }
                                checked={values.isOnlyFavourites}
                              />
                              <Translator
                                file="module-chemicalTrading"
                                name="show.only.favourite.products"
                              />
                            </label>
                          </div>
                          <div className="col-md-3 text-md-center">
                            <label className="col-form-label mr-3 pointer">
                              <input
                                type="checkbox"
                                className="mr-1"
                                value={values.isOnlyOwn}
                                onChange={() =>
                                  setFieldValue('isOnlyOwn', !values.isOnlyOwn)
                                }
                                checked={values.isOnlyOwn}
                              />
                              <Translator
                                file="module-chemicalTrading"
                                name="show.only.my.products"
                              />
                            </label>
                          </div>
                          <div className="col-md-3 text-md-center">
                            <label className="col-form-label mr-3 pointer">
                              <input
                                type="checkbox"
                                className="mr-1"
                                value={values.isOnlyArchives}
                                onChange={() =>
                                  setFieldValue(
                                    'isOnlyArchives',
                                    !values.isOnlyArchives,
                                  )
                                }
                                checked={values.isOnlyArchives}
                              />
                              <Translator
                                file="module-chemicalTrading"
                                name="show.only.archived.products"
                              />
                            </label>
                          </div>
                          <div className="col-md-3 text-md-center">
                            <label className="col-form-label mr-3 pointer">
                              <input
                                type="checkbox"
                                className="mr-1"
                                value={values.archivesToo}
                                onChange={() =>
                                  setFieldValue(
                                    'archivesToo',
                                    !values.archivesToo,
                                  )
                                }
                                checked={values.archivesToo}
                              />
                              <Translator
                                file="module-chemicalTrading"
                                name="show.archived.products.too"
                              />
                            </label>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <AppInput
                              icon={<AppRemixIcon icon="building-4" />}
                              title={
                                <Translator
                                  file="module-chemicalTrading"
                                  name="company"
                                />
                              }
                              onChange={(v) => setFieldValue('company', v)}
                              value={values.company}
                              isClearable
                            />
                          </div>
                          <div className="col-md-6">
                            <AppInput
                              icon={
                                <AppRemixIcon icon="font-color" noVariants />
                              }
                              title={
                                <Translator
                                  file="module-chemicalTrading"
                                  name="comercial.name"
                                />
                              }
                              onChange={(v) =>
                                setFieldValue('comercialName', v)
                              }
                              value={values.comercialName}
                              isClearable
                            />
                          </div>
                          <div className="col-md-6">
                            <AppInput
                              icon={
                                <AppRemixIcon icon="font-color" noVariants />
                              }
                              title={
                                <Translator
                                  file="module-chemicalTrading"
                                  name="chemical.name"
                                />
                              }
                              onChange={(v) => setFieldValue('chemicalName', v)}
                              value={values.chemicalName}
                              isClearable
                            />
                          </div>
                          <div className="col-md-6">
                            <AppSingleSelect
                              options={productOptions?.containerTypes}
                              icon={<AppRemixIcon icon="apps-2" />}
                              onChange={(v) =>
                                setFieldValue('containerType', v)
                              }
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="container.type"
                                />,
                              )}
                              value={values.containerType}
                            />
                          </div>
                          <div className="col-md-6">
                            <AppInputNumber
                              title={`${ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="quantity"
                                />,
                              )} (${ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="from"
                                />,
                              )})`}
                              icon={<AppRemixIcon icon="hashtag" noVariants />}
                              onChange={(v) => setFieldValue('quantityFrom', v)}
                              value={values && values?.quantityFrom}
                              isClearable
                            />
                          </div>
                          <div className="col-md-6">
                            <AppInputNumber
                              title={`${ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="quantity"
                                />,
                              )} (${ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="to"
                                />,
                              )})`}
                              icon={<AppRemixIcon icon="hashtag" noVariants />}
                              onChange={(v) => setFieldValue('quantityTo', v)}
                              value={values && values?.quantityTo}
                              isClearable
                            />
                          </div>
                          <div className="col-md-6">
                            <AppDatePicker
                              title={`${ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="upload.date"
                                />,
                              )} (${ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="from"
                                />,
                              )})`}
                              value={values.dateFrom}
                              onChange={(date) =>
                                setFieldValue('dateFrom', date)
                              }
                              showMonthDropdown
                              showYearDropdown
                              isClearable
                            />
                          </div>
                          <div className="col-md-6">
                            <AppDatePicker
                              title={`${ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="upload.date"
                                />,
                              )} (${ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="to"
                                />,
                              )})`}
                              value={values.dateTo}
                              onChange={(date) => setFieldValue('dateTo', date)}
                              showMonthDropdown
                              showYearDropdown
                              isClearable
                            />
                          </div>
                          <div className="col-md-6">
                            <AppDatePicker
                              title={`${ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="expire.date"
                                />,
                              )} (${ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="from"
                                />,
                              )})`}
                              value={values.expireDateFrom}
                              onChange={(date) =>
                                setFieldValue('expireDateFrom', date)
                              }
                              showMonthDropdown
                              showYearDropdown
                              isClearable
                            />
                          </div>
                          <div className="col-md-6">
                            <AppDatePicker
                              title={`${ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="expire.date"
                                />,
                              )} (${ReactDOMServer.renderToString(
                                <Translator
                                  file="module-chemicalTrading"
                                  name="to"
                                />,
                              )})`}
                              value={values.expireDateTo}
                              onChange={(date) =>
                                setFieldValue('expireDateTo', date)
                              }
                              showMonthDropdown
                              showYearDropdown
                              isClearable
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-12">
                            <div className="float-right">
                              <span
                                className="btn btn-info btn-sm"
                                onClick={handleSubmit}
                              >
                                <Translator
                                  file="module-chemicalTrading"
                                  name="filter"
                                />
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Collapse>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body">
                      {loading ? (
                        <NoInfo icon={<SpinnerLoader size={40} />} />
                      ) : (
                        <DataTable
                          columns={columns}
                          data={products}
                          striped
                          responsive
                          noHeader
                          noContextMenu
                          noDataComponent={ReactDOMServer.renderToString(
                            <Translator
                              file="module-chemicalTrading"
                              name="no.products"
                            />,
                          )}
                          highlightOnHover
                          pointerOnHover
                          onRowClicked={(product) =>
                            navigate(
                              path({
                                name: 'user_module_chemicalTrading_product_home',
                                params: { moduleBridgeHex, id: product.id },
                              }),
                            )
                          }
                          conditionalRowStyles={conditionalRowStyles}
                          pagination
                          paginationPerPage={10}
                          paginationComponentOptions={{
                            rangeSeparatorText: 'de',
                            noRowsPerPage: true,
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </Formik>
    </div>
  );
};

export default ModuleChemicalTradingProductListScreen;

const ClientLogo = styled.img`
  border-radius: 2px;
  border: none;
  box-shadow: 2px 2px 3px lightgray;
  max-width: 150px;
`;
const Image = styled.div`
  width: 65px;
  height: 65px;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  background-image: url(${(props) => props.src});
`;
const OptionsCell = styled.div`
  width: 100px;
  display: flex;
  justify-content: end;
`;
